import clsx from 'clsx'
import React, { Children, isValidElement, useRef, useState, useLayoutEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'

interface TooltipProps {
  content?: string
  children?: React.ReactNode
  text?: string
  className?: string
  position?: 'top' | 'bottom'
  tooltipClassName?: string
  hide?: boolean
  config?: {
    maxWidth?: string
    delay?: number
    allowHTML?: boolean
  }
}

interface Coords {
  top: number
  left: number
}

export const Tooltip = ({
  content,
  children,
  text,
  className,
  position = 'top',
  tooltipClassName,
  hide,
  config,
}: TooltipProps) => {
  const triggerRef = useRef<HTMLDivElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(false)
  const [coords, setCoords] = useState<Coords>({ top: 0, left: 0 })

  const childrenArray = Children.toArray(children)

  const slotContent = childrenArray.find(
    (child: any) => isValidElement(child) && (child as any).props?.slot === 'tooltip-content',
  ) as React.ReactElement | undefined

  const triggerChildren = childrenArray.filter(child => child !== slotContent)

  const tooltipBody = slotContent ? (
    slotContent
  ) : config?.allowHTML && typeof content === 'string' ? (
    <span dangerouslySetInnerHTML={{ __html: content }} />
  ) : (
    content
  )

  const hasTooltip = Boolean(slotContent || content)
  const portalTarget = (typeof document !== 'undefined' && document.getElementById('cop-root')) || document.body

  // ---------------------------
  // Compute position relative to viewport (so it escapes any
  // overflow:hidden / table clipping from ancestors)
  // ---------------------------
  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current
    const tooltip = tooltipRef.current
    if (!trigger) return

    const triggerRect = trigger.getBoundingClientRect()
    const tooltipRect = tooltip?.getBoundingClientRect()
    const tooltipHeight = tooltipRect?.height ?? 0
    const tooltipWidth = tooltipRect?.width ?? 0

    const GAP = 8

    let top = position === 'top' ? triggerRect.top - tooltipHeight - GAP : triggerRect.bottom + GAP

    let left = triggerRect.left + triggerRect.width / 2 - tooltipWidth / 2

    // Keep within viewport horizontally
    const PADDING = 8
    if (left < PADDING) left = PADDING
    if (left + tooltipWidth > window.innerWidth - PADDING) {
      left = window.innerWidth - tooltipWidth - PADDING
    }

    // Flip vertically if it would overflow the viewport
    if (top < PADDING && position === 'top') {
      top = triggerRect.bottom + GAP
    }
    if (top + tooltipHeight > window.innerHeight - PADDING && position === 'bottom') {
      top = triggerRect.top - tooltipHeight - GAP
    }

    setCoords({ top, left })
  }, [position])

  const showTooltip = () => {
    if (hide || !hasTooltip) return
    setVisible(true)
  }

  const hideTooltip = () => setVisible(false)

  // Recompute once visible (after the tooltip node has dimensions),
  // and keep it pinned to the trigger on scroll/resize.
  useLayoutEffect(() => {
    if (!visible) return

    updatePosition()

    const handleReposition = () => updatePosition()
    window.addEventListener('scroll', handleReposition, true)
    window.addEventListener('resize', handleReposition)

    return () => {
      window.removeEventListener('scroll', handleReposition, true)
      window.removeEventListener('resize', handleReposition)
    }
  }, [visible, updatePosition])

  return (
    <div ref={triggerRef} className={clsx('relative', className)} onMouseEnter={showTooltip} onMouseLeave={hideTooltip}>
      {triggerChildren.length > 0 ? triggerChildren : text}

      {hasTooltip &&
        visible &&
        createPortal(
          <div
            ref={tooltipRef}
            style={{
              position: 'fixed',
              top: coords.top,
              left: coords.left,
              maxWidth: config?.maxWidth,
              transitionDelay: config?.delay ? `${config.delay}ms` : undefined,
              zIndex: 9999,
            }}
            className={clsx(
              'py-[6px] px-2 bg-[#505050] rounded-lg shadow-lg opacity-100 visible',
              'text-wrap whitespace-pre-wrap w-max',
              tooltipClassName,
            )}
          >
            <div className="text-[#191919] text-xs leading-5 text-center">{tooltipBody}</div>

            {/* Arrow */}
            <div
              className={clsx(
                'absolute left-1/2 -translate-x-1/2 size-2 rounded-[1px] bg-[#505050] shadow-lg',
                position === 'top' ? 'top-full -mt-1 rotate-45' : 'bottom-full -mb-1 -rotate-45',
              )}
            />
          </div>,
          portalTarget,
        )}
    </div>
  )
}
